"use client";

import { useSyncExternalStore } from "react";

/**
 * A hook that returns true once the component has mounted on the client.
 * Useful for avoiding hydration mismatches when rendering client-only content.
 *
 * Backed by `useSyncExternalStore`: the server snapshot is `false` and the
 * client snapshot is `true`, so React produces a correct hydration pass
 * without an extra render-then-effect flip (avoids the brief flash a
 * useState/useEffect implementation causes).
 *
 * @returns true if running on the client, false during SSR
 *
 * @example
 * ```tsx
 * const isClient = useIsClient();
 *
 * if (!isClient) {
 *   return null; // or a skeleton/placeholder
 * }
 *
 * return <ClientOnlyComponent />;
 * ```
 */
function subscribe(): () => void {
	return () => {};
}

function getClientSnapshot(): boolean {
	return true;
}

function getServerSnapshot(): boolean {
	return false;
}

export function useIsClient(): boolean {
	return useSyncExternalStore(
		subscribe,
		getClientSnapshot,
		getServerSnapshot,
	);
}
